<!-- PunchList.vue -->
<script lang="ts" setup generic="TOrder extends number | string">
import { computed, onBeforeMount } from 'vue';
import { DisplayStyle, IconAnimation, IconTypes, TextDisplayStyle } from '../../Enums/UiEnums';
import { bootstrapTextClass } from '../../lib/StyleHelpers';
import Icon from '../Icon.vue';
import { PunchlistItem, PunchlistItemState, PunchlistStateType, PunchListState } from './PunchListModels';

    const props = defineProps<{
        punchList: PunchListState<TOrder>
    }>();

    const emits = defineEmits<{
        (e: "state", state: PunchlistStateType|undefined): void
    }>();

    interface StateInfo {
        icon: IconTypes,
        animation?: IconAnimation,
        displayStyle: DisplayStyle,
        title: string
    }

    const stateInfo: Record<PunchlistItemState, StateInfo> = {
        [PunchlistItemState.ToDo]: { icon: IconTypes.CircleHollow, displayStyle: DisplayStyle.Secondary, title: "No started" },
        [PunchlistItemState.Complete]: { icon: IconTypes.CircleCheck, displayStyle: DisplayStyle.Success, title: "Completed successfully" },
        [PunchlistItemState.Processing]: { icon: IconTypes.SpinnerDots, displayStyle: DisplayStyle.Primary, animation: IconAnimation.spin, title: "Processing..." },
        [PunchlistItemState.Error]: { icon: IconTypes.CircleExclamation, displayStyle: DisplayStyle.Danger, title: "Error processing" }
    }

    onBeforeMount(() => {
        props.punchList.onStateChange = stateChangeSink;
    });

    function getStateInfo(item: PunchlistItem<TOrder>): StateInfo 
    {
        return stateInfo[item.state ?? PunchlistItemState.ToDo];
    }

    function titleDisplayState(item: PunchlistItem<TOrder>): string|undefined
    {
        if (item.state === PunchlistItemState.ToDo)
            return bootstrapTextClass(TextDisplayStyle.Muted);
        return;
    }

    const visibleItems = computed<PunchlistItem<TOrder>[]>(() => 
        props.punchList?.punchListItems.filter(i => false === (i.hidden ?? false)) ?? []);

    function stateChangeSink(puchlistState?: PunchlistStateType)
    {
        emits("state", puchlistState);
    }        

</script>

<style>
    .grid-container {
        display: grid;
        grid-template-columns: auto 1fr; /* First column auto-sizes, second column fills the rest */
        padding: 10px;
    }

    .grid-item {
        border-bottom: solid 1px lightgray;
        padding-top: 0.5em;
        padding-bottom: 0.5em;
    }

    .grid-item:nth-child(even) {
        padding-left: 1em;
    }
</style>

<template>

    <div class="grid-container">
        <template v-for="{item, stateInfo} in visibleItems.map(i => ({item: i, stateInfo: getStateInfo(i) }))">
            <div class="grid-item">
                <Icon :icon="stateInfo.icon" :animation="stateInfo.animation" :displayStyle="stateInfo.displayStyle" :title="stateInfo.title" class="ms-1 me-1" />
            </div>
            <div class="grid-item">
                <slot name="title" :item="item">
                    <span :class="titleDisplayState(item)">
                        {{ item.title }}
                    </span>
                </slot>
                <slot name="subTitle" :item="item">
                    <div class="text-muted ms-1">
                        <small>
                            {{ item.subTitle }}
                        </small>
                    </div>
                </slot>
            </div>
        </template>
    </div>
</template>